import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getTransformedUserBorrowList } from '@/shared/utils/helpers';
import { BorrowStatusType } from '@/shared/api/borrow';
import { Store } from '@/shared/store';
import { useCentrifugeContext } from '@/app/providers/Centrifuge';

export const useUserBorrowItems = (selectedBorrowStatusList: BorrowStatusType) => {
    const activeWalletAddress = useRef<string | ''>('');

    const { walletAddress } = useSelector((state: Store) => ({
        walletAddress: state.wallets.active?.address,
    }));

    const { userBorrowsOpened, userBorrowsSuspended, userBorrowsRegistered, userBorrowsClosed } =
        useSelector((state: Store) => ({
            userBorrowsOpened: state.borrows.userBorrowsOpened,
            userBorrowsSuspended: state.borrows.userBorrowsSuspended,
            userBorrowsRegistered: state.borrows.userBorrowsRegistered,
            userBorrowsClosed: state.borrows.userBorrowsClosed,
        }));

    const { subscribeToChannel, subscribedChannels, unsubscribeFromAllChannels, isReady } =
        useCentrifugeContext();

    const transformedOpenedBorrows = useMemo(
        () => getTransformedUserBorrowList(userBorrowsOpened),
        [userBorrowsOpened],
    );

    const transformedSuspendedBorrows = useMemo(
        () => getTransformedUserBorrowList([...userBorrowsSuspended, ...userBorrowsRegistered]),
        [userBorrowsSuspended, userBorrowsRegistered],
    );

    const transformedClosedBorrows = useMemo(
        () => getTransformedUserBorrowList(userBorrowsClosed),
        [userBorrowsClosed],
    );

    const items = useMemo(() => {
        switch (selectedBorrowStatusList) {
            case BorrowStatusType.OPENED:
                return transformedOpenedBorrows;
            case BorrowStatusType.SUSPENDED:
                return transformedSuspendedBorrows;
            case BorrowStatusType.CLOSED:
                return transformedClosedBorrows;
            default:
                return [];
        }
    }, [
        selectedBorrowStatusList,
        transformedOpenedBorrows,
        transformedSuspendedBorrows,
        transformedClosedBorrows,
    ]);

    useEffect(() => {
        if (!walletAddress || !isReady) return;

        if (activeWalletAddress.current !== walletAddress) {
            unsubscribeFromAllChannels();
            activeWalletAddress.current = walletAddress;
        }

        if (selectedBorrowStatusList !== BorrowStatusType.OPENED) return;

        transformedOpenedBorrows.forEach((item) => {
            const channel = `maintenance:${item.address}`;

            if (!subscribedChannels.has(channel)) {
                subscribeToChannel(channel);
            }
        });
    }, [
        selectedBorrowStatusList,
        transformedOpenedBorrows,
        subscribeToChannel,
        walletAddress,
        isReady,
    ]);

    useEffect(() => {
        return () => {
            unsubscribeFromAllChannels();
        };
    }, []);

    return { items };
};
